import {
    SimpleGrid,
    LinkBox,
    LinkOverlay,
    Center,
    Image,
    Icon,
    Text,
    useColorModeValue,
    usePrefersReducedMotion,
    keyframes
} from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'

/*
 * One object per app — edit/add/remove a line here to change the row.
 *   label:    app name (bold title on the tile)
 *   blurb:    one-line description under the name
 *   iconSrc:  image path (served from /public) — takes precedence over `icon`
 *   icon:     react-icons component, used as a placeholder until a real logo exists
 *             (swap by replacing `icon: X` with `iconSrc: '/app-icons/name.png'`)
 *   href:     destination URL or in-app route
 *   external: true = plain anchor (same tab), false = in-app SPA navigation
 */
const APP_LAUNCHER_ITEMS = [
    { id: 'apex', label: 'Apex', blurb: 'Your Business Intelligence', iconSrc: '/app-icons/apex.png', href: 'https://forecast.fhl.one', external: true },
    { id: 'launchpad', label: 'Launchpad', blurb: 'All Your Training', iconSrc: '/app-icons/launchpad.png', href: 'https://launchpad.fhl.one', external: true },
    { id: 'origin', label: 'Origin', blurb: 'Automated Past-Client Follow-Up', iconSrc: '/app-icons/origin.png', href: '/crm', external: false }
]

// Soft looping glow: resting shadow matches Chakra's `md`, breathing up to a soft blue ring.
const glowPulse = keyframes`
    0%, 100% {
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 0 0 0 rgba(66, 153, 225, 0);
    }
    50% {
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06), 0 0 18px 2px rgba(66, 153, 225, 0.45);
    }
`

export default function AppLauncherRow() {
    const tileBg = useColorModeValue('white', 'gray.800')
    const blurbColor = useColorModeValue('gray.500', 'gray.400')
    const iconColor = useColorModeValue('blue.600', 'blue.300')
    const prefersReducedMotion = usePrefersReducedMotion()

    const pulseAnimation = prefersReducedMotion ? undefined : `${glowPulse} 2.6s ease-in-out infinite`

    return (
        <SimpleGrid
            columns={{ base: 1, sm: 2, lg: Math.min(APP_LAUNCHER_ITEMS.length, 4) }}
            spacing='20px'
            mt={2}
            mb={4}
            px={1}
        >
            {APP_LAUNCHER_ITEMS.map((item) => (
                <LinkBox
                    key={item.id}
                    role='group'
                    p={6}
                    bg={tileBg}
                    boxShadow='md'
                    rounded='xl'
                    minH='190px'
                    textAlign='center'
                    transition='all 0.2s ease'
                    animation={pulseAnimation}
                    _hover={{ boxShadow: 'lg', transform: 'translateY(-2px)', animation: 'none' }}
                >
                    <Center h='72px' mb={3}>
                        {item.iconSrc
                            ? <Image src={item.iconSrc} alt={`${item.label} logo`} boxSize='64px' objectFit='contain' />
                            : <Icon as={item.icon} boxSize='56px' color={iconColor} />
                        }
                    </Center>
                    {item.external
                        ? <LinkOverlay href={item.href}>
                            <Text fontWeight='bold' fontSize='xl'>{item.label}</Text>
                        </LinkOverlay>
                        : <LinkOverlay as={RouterLink} to={item.href}>
                            <Text fontWeight='bold' fontSize='xl'>{item.label}</Text>
                        </LinkOverlay>
                    }
                    <Text mt={1} fontSize='sm' color={blurbColor}>{item.blurb}</Text>
                </LinkBox>
            ))}
        </SimpleGrid>
    )
}
